/**
 * Minimal TipTap editor with @mention support for bid members — and, when the
 * host passes `nonMemberCandidates`, for colleagues not on the bid yet (marked
 * "Not on this bid"; the host asks before posting whether to add them).
 *
 * Extracted from `CommentComposer` so every comment-writing surface (new
 * comment, reply, edit) shares one editor, one mention popup and one
 * serialization format: mentions round-trip as `@[Full Name](user:<uuid>)`
 * tokens via `@/lib/comment-mentions`.
 *
 * The editor owns no submit chrome — the host renders its own buttons and
 * reads the body through the imperative handle (or the Cmd/Ctrl+Enter
 * `onSubmit` callback).
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useEditor, EditorContent, type Content } from '@tiptap/react'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Placeholder from '@tiptap/extension-placeholder'
import Mention, { type MentionNodeAttrs } from '@tiptap/extension-mention'
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion'
import { MemberAvatar, type BidMemberInfo } from '@/components/ui/owner-assignment'
import { getUserDisplayName } from '@/lib/user-display'
import { useAuthStore } from '@/stores/auth-store'
import { parseBodyToDoc, serializeDocToBody } from '@/lib/comment-mentions'
import { buildMentionItems, type MentionCandidate } from '@/lib/mention-invites'
import { computeMentionPopupPosition } from '@/lib/mention-popup'
import { cn } from '@/lib/utils'

export interface MentionEditorProps {
  /** Bid members offered as @mention suggestions. Empty → no suggestions. */
  bidMembers: BidMemberInfo[]
  /**
   * Organisation members who are not on the bid, offered after the bid members
   * and marked "Not on this bid". Only passed when the author may add people.
   */
  nonMemberCandidates?: MentionCandidate[]
  /** Every document change, serialized to mention-token text. */
  onBodyChange?: (bodyWithTokens: string) => void
  /** Mention-token text used to seed the document (edit prefill). */
  initialValue?: string
  placeholder?: string
  /** Reply-box variant: single-line min-height. */
  compact?: boolean
  /** Cmd/Ctrl+Enter. Not fired when the document is blank. */
  onSubmit: (bodyWithTokens: string) => void
  /** Escape, when the mention popup is closed. */
  onCancel?: () => void
  autoFocus?: boolean
  /** Lets the host disable its submit button while the document is blank. */
  onEmptyChange?: (isEmpty: boolean) => void
}

export interface MentionEditorHandle {
  /** Current document serialized to mention-token text. */
  getBody: () => string
  clear: () => void
  focus: () => void
}

interface PopupState {
  items: MentionCandidate[]
  index: number
  caretTop: number
  caretBottom: number
  caretLeft: number
}

export const MentionEditor = forwardRef<MentionEditorHandle, MentionEditorProps>(
  function MentionEditor(
    {
      bidMembers,
      nonMemberCandidates,
      onBodyChange,
      initialValue,
      placeholder = 'Leave a comment…',
      compact = false,
      onSubmit,
      onCancel,
      autoFocus = true,
      onEmptyChange,
    },
    ref,
  ) {
    const currentUserId = useAuthStore((s) => s.user?.id ?? null)
    const [popup, setPopup] = useState<PopupState | null>(null)

    // Stable refs so the editor's captured closures read fresh values even
    // though `useEditor` builds the extensions once.
    const bidMembersRef = useRef(bidMembers)
    bidMembersRef.current = bidMembers
    const nonMembersRef = useRef(nonMemberCandidates ?? [])
    nonMembersRef.current = nonMemberCandidates ?? []
    const onBodyChangeRef = useRef(onBodyChange)
    onBodyChangeRef.current = onBodyChange
    const onSubmitRef = useRef(onSubmit)
    onSubmitRef.current = onSubmit
    const onCancelRef = useRef(onCancel)
    onCancelRef.current = onCancel
    const onEmptyChangeRef = useRef(onEmptyChange)
    onEmptyChangeRef.current = onEmptyChange
    const submitRef = useRef<() => void>(() => {})
    const popupOpenRef = useRef(false)
    const isEmptyRef = useRef(true)

    // Live controller for the mention suggestion popup (mutated by the
    // suggestion render handlers, read by the popup UI + keyboard nav).
    const commandRef = useRef<((sel: MentionNodeAttrs) => void) | null>(null)
    const itemsRef = useRef<MentionCandidate[]>([])
    const indexRef = useRef(0)

    const closePopup = () => {
      popupOpenRef.current = false
      commandRef.current = null
      setPopup(null)
    }

    const selectMember = (member: MentionCandidate | undefined) => {
      if (!member) return
      const label = member.fullName || member.email || 'Someone'
      commandRef.current?.({ id: member.userId, label })
    }

    const suggestion: Omit<SuggestionOptions<MentionCandidate, MentionNodeAttrs>, 'editor'> = useMemo(
      () => ({
        items: ({ query }) => buildMentionItems(bidMembersRef.current, nonMembersRef.current, query),
        render: () => {
          const syncPopup = (props: SuggestionProps<MentionCandidate, MentionNodeAttrs>) => {
            itemsRef.current = props.items
            commandRef.current = props.command
            indexRef.current = Math.min(indexRef.current, Math.max(0, props.items.length - 1))
            const rect = props.clientRect?.()
            popupOpenRef.current = props.items.length > 0
            if (!rect || props.items.length === 0) {
              setPopup(null)
              return
            }
            setPopup({
              items: props.items,
              index: indexRef.current,
              caretTop: rect.top,
              caretBottom: rect.bottom,
              caretLeft: rect.left,
            })
          }
          return {
            onStart: (props) => {
              indexRef.current = 0
              syncPopup(props)
            },
            onUpdate: (props) => syncPopup(props),
            onKeyDown: ({ event }) => {
              const items = itemsRef.current
              if (event.key === 'ArrowDown') {
                if (items.length === 0) return false
                indexRef.current = (indexRef.current + 1) % items.length
                setPopup((p) => (p ? { ...p, index: indexRef.current } : p))
                return true
              }
              if (event.key === 'ArrowUp') {
                if (items.length === 0) return false
                indexRef.current = (indexRef.current - 1 + items.length) % items.length
                setPopup((p) => (p ? { ...p, index: indexRef.current } : p))
                return true
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                if (items.length === 0) return false
                selectMember(items[indexRef.current])
                return true
              }
              if (event.key === 'Escape') {
                closePopup()
                return true
              }
              return false
            },
            onExit: () => closePopup(),
          }
        },
      }),
      // Handlers read from refs; rebuild is unnecessary and would recreate the editor.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    )

    // Seed once — `initialValue` is an initial document, not a controlled value.
    const [initialDoc] = useState<Content>(() =>
      initialValue ? (parseBodyToDoc(initialValue) as Content) : null,
    )

    const emitEmpty = (isEmpty: boolean) => {
      if (isEmptyRef.current === isEmpty) return
      isEmptyRef.current = isEmpty
      onEmptyChangeRef.current?.(isEmpty)
    }

    const editor = useEditor({
      autofocus: autoFocus ? 'end' : false,
      content: initialDoc,
      extensions: [
        Document,
        Paragraph,
        Text,
        Placeholder.configure({ placeholder }),
        Mention.configure({
          HTMLAttributes: {
            class: 'rounded bg-muted px-1 text-foreground',
          },
          suggestion,
        }),
      ],
      editorProps: {
        attributes: {
          'data-compact': compact ? 'true' : 'false',
          class: cn(
            'max-h-40 overflow-y-auto text-sm outline-none px-2 py-1.5 [&_p.is-editor-empty:first-child::before]:text-muted-foreground [&_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_p.is-editor-empty:first-child::before]:float-left [&_p.is-editor-empty:first-child::before]:pointer-events-none [&_p.is-editor-empty:first-child::before]:h-0',
            compact ? 'min-h-[36px]' : 'min-h-[60px]',
          ),
        },
        handleKeyDown: (_view, event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault()
            submitRef.current()
            return true
          }
          if (event.key === 'Escape' && !popupOpenRef.current) {
            if (!onCancelRef.current) return false
            event.preventDefault()
            onCancelRef.current()
            return true
          }
          return false
        },
      },
      onUpdate: ({ editor: ed }) => {
        emitEmpty(ed.getText().trim().length === 0)
        onBodyChangeRef.current?.(serializeDocToBody(ed.getJSON() as never))
      },
    })

    const getBody = () => (editor ? serializeDocToBody(editor.getJSON() as never) : '')

    submitRef.current = () => {
      const body = getBody()
      if (!body.trim()) return
      onSubmitRef.current(body)
    }

    useImperativeHandle(
      ref,
      () => ({
        getBody,
        clear: () => {
          editor?.commands.clearContent()
          emitEmpty(true)
        },
        focus: () => editor?.commands.focus('end'),
      }),
      // `editor` is the only dependency that changes identity here.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [editor],
    )

    // Tell the host about the seeded document's empty-state on mount.
    useEffect(() => {
      if (!editor) return
      emitEmpty(editor.getText().trim().length === 0)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor])

    return (
      <>
        <EditorContent editor={editor} />
        {popup && popup.items.length > 0 && (
          <MentionPopup
            popup={popup}
            currentUserId={currentUserId}
            onSelect={(m) => selectMember(m)}
          />
        )}
      </>
    )
  },
)

export function MentionPopup({
  popup,
  currentUserId,
  onSelect,
}: {
  popup: PopupState
  currentUserId: string | null
  onSelect: (member: MentionCandidate) => void
}) {
  const { top, left, maxHeight } = computeMentionPopupPosition({
    caretTop: popup.caretTop,
    caretBottom: popup.caretBottom,
    caretLeft: popup.caretLeft,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  })

  return createPortal(
    <div
      style={{ position: 'fixed', top, left, maxHeight, zIndex: 70 }}
      className="w-56 overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-lg"
      onMouseDown={(e) => e.preventDefault()}
    >
      {popup.items.map((member, i) => {
        const label = getUserDisplayName(
          { userId: member.userId, fullName: member.fullName, email: member.email },
          currentUserId,
        )
        return (
          <button
            key={member.userId}
            type="button"
            onClick={() => onSelect(member)}
            className={cn(
              'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors text-left',
              i === popup.index && 'bg-muted',
            )}
          >
            <MemberAvatar user={member} />
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{label}</div>
              {member.notOnBid ? (
                <div className="text-meta truncate">
                  Not on this bid{member.fullName ? ` · ${member.email}` : ''}
                </div>
              ) : (
                member.fullName && <div className="text-meta truncate">{member.email}</div>
              )}
            </div>
          </button>
        )
      })}
    </div>,
    document.body,
  )
}
